<template>
    <template v-if="isMobile && !isDisallowedRoute">
        <Transition name="mini-ticket-transition">
            <div
                class="mini-ticket"
                :class="{ 'mini-ticket--open': showMiniTicket && ticketOutcomeId }"
                v-show="showMiniTicket && ticketOutcomeId"
            >
                <div class="mini-ticket__controls">
                    <button class="mini-ticket__button" :disabled="sendingInProgress" @click="handleMiniticketHide">
                        <SvgIcon name="dropdown-down" />
                    </button>
                    <button
                        class="mini-ticket__button"
                        :disabled="sendingInProgress"
                        @click="ticketStore.clearTicket(false)"
                    >
                        <SvgIcon name="delete-remove" />
                    </button>
                    <RouterLink :to="PATHS.TICKET" class="mini-ticket__button">
                        <SvgIcon name="new-tab" />
                    </RouterLink>
                </div>
                <TicketItem
                    class="dark-mode"
                    v-if="ticketOutcomeId"
                    :outcome-id="ticketOutcomeId"
                    :key="ticketOutcomeId"
                />
                <div class="mini-ticket__price">
                    <!-- v-if is used here intentionally so the stake price stays reactive -->
                    <TicketPriceInput mini-ticket hide-input-error v-if="ticketOutcomeId" />
                    <TicketFooter mini-ticket @on-place-bet="resetDisplayedErrors" />
                </div>
            </div>
        </Transition>
        <MiniTicketPreview />
    </template>
</template>

<script lang="ts" setup>
import { computed, ComputedRef, Ref, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import SvgIcon from "@/shared/components/atoms/SvgIcon.vue";
import TicketItem from "@/ticket/components/atoms/TicketItem.vue";
import MiniTicketPreview from "@/ticket/components/molecules/MiniTicketPreview.vue";
import TicketPriceInput from "@/ticket/components/atoms/TicketPriceInput.vue";
import TicketFooter from "@/ticket/components/molecules/TicketFooter.vue";
import { useTicketStore } from "@/ticket/stores/use-ticket-store";
import useIsBreakpoint from "@/shared/composables/use-is-breakpoint";
import { PATHS } from "@/router/paths";
import useSazkaSnackbar from "@/shared/composables/use-sazka-snackbar";
import analytics from "@/shared/analytics";
import type { TicketError } from "@/ticket/model/ticket-error";
import useConfirmModal from "@/shared/composables/use-confirm-modal";
import { errorDictionary } from "@/shared/model/ticket";

const ticketStore = useTicketStore();

const route = useRoute();
const router = useRouter();

const { isMobile } = useIsBreakpoint();

const showMiniTicket = ref(false);
const displayedErrorIds: Ref<string[]> = ref([]);

const ticketId: ComputedRef<string> = computed(() => ticketStore.slipBet?.data?.bets?.[0]?.id);
const ticketOutcomeId: ComputedRef<string> = computed(() => ticketStore.outcomeIds?.[0]);
const sendingInProgress: ComputedRef<boolean> = computed(() => Boolean(ticketStore.sendingInProgress));
const errors: ComputedRef<TicketError[]> = computed(() => ticketStore.errors?.filter((e) => !e.dismissed));
const isDisallowedRoute: ComputedRef<boolean> = computed(() => route.name === "Ticket" || route.name === "MyTickets");

const ticketPath: ComputedRef<string | undefined> = computed(() =>
    ticketId.value ? `${PATHS.MY_TICKETS}?tiket=${ticketId.value}` : undefined,
);

const miniTicketErrors: ComputedRef<TicketError[]> = computed(() =>
    isMobile.value && showMiniTicket.value
        ? errors.value?.filter((e) => !["OUTCOME_SUSPENDED", "OUTCOME_RESULTED"].includes(e.errorId)) // Errors filtered out are handled differently on mini ticket
        : [],
);

watch(miniTicketErrors, (newValue) => {
    // TODO: Design in Figma does not count with multiple errors in comparison with regular ticket... or should we display multiple modals? That's stupid.
    const err = newValue?.[0];
    if (err && !displayedErrorIds.value.includes(err.id)) {
        const { openModal } = useConfirmModal();
        openModal({
            title: sendingInProgress.value ? "Tiket nebyl vsazen" : "Tiket nemůže být vsazen",
            text: err?.errorId === "BET_PRICE_CHANGED" ? errorDictionary[err.errorId + "_MINI_TICKET"] : err.message,
            submitText: err?.errorId === "BET_PRICE_CHANGED" ? "Rozumím" : err?.buttons?.[0]?.text,
            submitAction: err?.buttons?.[0]?.action,
            submit2Text: err?.buttons?.[1]?.text,
            submit2Action: err?.buttons?.[1]?.action,
        });
        displayedErrorIds.value.push(err.id);
    }
});

const resetDisplayedErrors = () => {
    displayedErrorIds.value = [];
};

watch(ticketOutcomeId, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        resetDisplayedErrors();
    }
});

watch(
    () => ticketStore.originalOutcomes,
    (newValue, oldValue) => {
        if (!ticketStore.optionDisplayMiniTicket) return;

        if (newValue?.length === 1 && !!oldValue && oldValue?.length < 2) {
            showMiniTicket.value = true;
        } else {
            showMiniTicket.value = false;
        }
    },
);

watch(
    () => ticketStore.placed,
    (newValue) => {
        if (newValue && showMiniTicket.value) {
            useSazkaSnackbar().createSnackbar({
                message: "Tiket byl vsazen",
                type: "success",
                duration: 4000,
                icon: "win",
                button: {
                    label: "Zobrazit tiket",
                    onClick: () => {
                        router.push(ticketPath.value);
                        ticketStore.clearSlipBet();
                    },
                },
            });
        }
    },
);

watch(route, () => {
    showMiniTicket.value = false;
});

function handleMiniticketHide() {
    showMiniTicket.value = false;
    analytics.sendCollapsingDetail({
        eventProperty1: "miniticket",
        eventProperty2: "",
    });
}
</script>
